import React, { useState } from "react";
import {
  Box,
  Paper,
  Grid,
  TextField,
  MenuItem,
  InputAdornment,
  Typography,
  Button,
  IconButton,
  Avatar,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  Stack,
  FormControlLabel,
  Switch,
} from "@mui/material";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import BrokenImageIcon from "@mui/icons-material/BrokenImage";

// ---- Static option data (replace with API data as needed) ----
const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "draft", label: "Draft" },
];

const PURITY_OPTIONS = [
  "24K",
  "22K",
  "18K",
  "14K",
  "10K",
  "Silver 925",
  "Platinum 950",
];

const CATEGORY_DATA = {
  Rings: ["Engagement Rings", "Wedding Bands", "Cocktail Rings", "Men's Rings"],
  Necklaces: ["Chains", "Pendants", "Chokers", "Lockets"],
  Earrings: ["Studs", "Hoops", "Jhumkas", "Danglers"],
  Bangles: ["Kada", "Cuff Bangles", "Bridal Bangles"],
  Bracelets: ["Tennis Bracelets", "Charm Bracelets", "Chain Bracelets"],
};

const initialState = {
  name: "",
  description: "",
  price: "",
  offerPrice: "",
  status: "active",
  inStock: true, // stock is a binary in-stock / out-of-stock value
  weight: "",
  purity: "",
  category: "",
  subcategory: "",
  thumbnailUrl: "",
};

export default function ProductForm({ onSubmit }) {
  const [form, setForm] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [galleryUrls, setGalleryUrls] = useState([]); // array of image URL strings
  const [galleryInput, setGalleryInput] = useState("");

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setForm((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "category" ? { subcategory: "" } : {}),
    }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleStockChange = (event) => {
    setForm((prev) => ({ ...prev, inStock: event.target.checked }));
  };

  // Prevent typing negative numbers for price / offer price / weight
  const handleNonNegativeChange = (field) => (event) => {
    const value = event.target.value;
    if (value !== "" && Number(value) < 0) return; // ignore negative input
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const addGalleryUrl = () => {
    const url = galleryInput.trim();
    if (!url) return;
    setGalleryUrls((prev) => [...prev, url]);
    setGalleryInput("");
  };

  const removeGalleryUrl = (index) => {
    setGalleryUrls((prev) => prev.filter((_, i) => i !== index));
  };

  const validate = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = "Name is required";

    if (form.price === "") newErrors.price = "Price is required";
    else if (Number(form.price) < 0)
      newErrors.price = "Price cannot be negative";

    if (form.offerPrice !== "" && Number(form.offerPrice) < 0)
      newErrors.offerPrice = "Offer price cannot be negative";
    else if (form.offerPrice && Number(form.offerPrice) >= Number(form.price))
      newErrors.offerPrice = "Offer price must be less than price";

    if (form.weight === "") newErrors.weight = "Weight is required";
    else if (Number(form.weight) < 0)
      newErrors.weight = "Weight cannot be negative";

    if (!form.purity) newErrors.purity = "Purity is required";
    if (!form.category) newErrors.category = "Category is required";
    if (!form.subcategory) newErrors.subcategory = "Subcategory is required";
    if (!form.thumbnailUrl.trim())
      newErrors.thumbnailUrl = "Thumbnail image URL is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!validate()) return;

    const payload = {
      ...form,
      price: Number(form.price),
      offerPrice: form.offerPrice ? Number(form.offerPrice) : null,
      weight: Number(form.weight),
      inStock: Boolean(form.inStock),
      thumbnailUrl: form.thumbnailUrl.trim(),
      galleryUrls,
    };

    onSubmit ? onSubmit(payload) : console.log("Product payload:", payload);
  };

  const handleReset = () => {
    setForm(initialState);
    setGalleryUrls([]);
    setGalleryInput("");
    setErrors({});
  };

  const subcategoryOptions = form.category ? CATEGORY_DATA[form.category] : [];

  return (
    <Paper
      elevation={2}
      sx={{ p: { xs: 2, sm: 4 }, maxWidth: 900, mx: "auto", borderRadius: 3 }}
    >
      <Typography variant="h5" fontWeight={600} gutterBottom>
        Add / Edit Product
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Fill in the product details below.
      </Typography>

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Grid container spacing={3}>
          {/* Name */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Product Name"
              value={form.name}
              onChange={handleChange("name")}
              error={!!errors.name}
              helperText={errors.name}
              required
            />
          </Grid>

          {/* Status */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel id="status-label">Status</InputLabel>
              <Select
                labelId="status-label"
                label="Status"
                value={form.status}
                onChange={handleChange("status")}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Description */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Description"
              value={form.description}
              onChange={handleChange("description")}
            />
          </Grid>

          {/* Price */}
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              type="number"
              label="Price"
              value={form.price}
              onChange={handleNonNegativeChange("price")}
              error={!!errors.price}
              helperText={errors.price}
              inputProps={{ min: 0 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">₹</InputAdornment>
                ),
              }}
              required
            />
          </Grid>

          {/* Offer Price */}
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              type="number"
              label="Offer Price"
              value={form.offerPrice}
              onChange={handleNonNegativeChange("offerPrice")}
              error={!!errors.offerPrice}
              helperText={errors.offerPrice}
              inputProps={{ min: 0 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">₹</InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Stock: binary in-stock / out-of-stock */}
          <Grid item xs={12} sm={4}>
            <Typography
              variant="caption"
              color="text.secondary"
              component="div"
            >
              Stock
            </Typography>
            <FormControlLabel
              sx={{ mt: 0.5 }}
              control={
                <Switch checked={!!form.inStock} onChange={handleStockChange} />
              }
              label={form.inStock ? "In Stock" : "Out of Stock"}
            />
          </Grid>

          {/* Weight */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Weight"
              value={form.weight}
              onChange={handleNonNegativeChange("weight")}
              error={!!errors.weight}
              helperText={errors.weight}
              inputProps={{ min: 0 }}
              InputProps={{
                endAdornment: <InputAdornment position="end">g</InputAdornment>,
              }}
              required
            />
          </Grid>

          {/* Purity */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={!!errors.purity} required>
              <InputLabel id="purity-label">Purity</InputLabel>
              <Select
                labelId="purity-label"
                label="Purity"
                value={form.purity}
                onChange={handleChange("purity")}
              >
                {PURITY_OPTIONS.map((p) => (
                  <MenuItem key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Category */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={!!errors.category} required>
              <InputLabel id="category-label">Category</InputLabel>
              <Select
                labelId="category-label"
                label="Category"
                value={form.category}
                onChange={handleChange("category")}
              >
                {Object.keys(CATEGORY_DATA).map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {cat}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Subcategory */}
          <Grid item xs={12} sm={6}>
            <FormControl
              fullWidth
              error={!!errors.subcategory}
              required
              disabled={!form.category}
            >
              <InputLabel id="subcategory-label">Subcategory</InputLabel>
              <Select
                labelId="subcategory-label"
                label="Subcategory"
                value={form.subcategory}
                onChange={handleChange("subcategory")}
              >
                {subcategoryOptions.map((sub) => (
                  <MenuItem key={sub} value={sub}>
                    {sub}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
          </Grid>

          {/* Thumbnail image URL */}
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" gutterBottom>
              Thumbnail Image URL *
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar
                variant="rounded"
                src={form.thumbnailUrl || undefined}
                sx={{ width: 88, height: 88, bgcolor: "grey.100" }}
              >
                {!form.thumbnailUrl && (
                  <AddPhotoAlternateIcon color="disabled" />
                )}
              </Avatar>
              <TextField
                fullWidth
                size="small"
                placeholder="https://example.com/image.jpg"
                value={form.thumbnailUrl}
                onChange={handleChange("thumbnailUrl")}
                error={!!errors.thumbnailUrl}
                helperText={errors.thumbnailUrl}
              />
            </Stack>
          </Grid>

          {/* Gallery image URLs */}
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" gutterBottom>
              Gallery Image URLs
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="https://example.com/photo.jpg"
                value={galleryInput}
                onChange={(e) => setGalleryInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addGalleryUrl();
                  }
                }}
              />
              <Button
                variant="outlined"
                onClick={addGalleryUrl}
                startIcon={<AddIcon />}
              >
                Add
              </Button>
            </Stack>

            <Stack direction="row" flexWrap="wrap" gap={1.5}>
              {galleryUrls.map((url, index) => (
                <Box key={index} sx={{ position: "relative" }}>
                  <Avatar
                    variant="rounded"
                    src={url}
                    sx={{ width: 72, height: 72, bgcolor: "grey.100" }}
                  >
                    <BrokenImageIcon fontSize="small" color="disabled" />
                  </Avatar>
                  <IconButton
                    size="small"
                    onClick={() => removeGalleryUrl(index)}
                    sx={{
                      position: "absolute",
                      top: -8,
                      right: -8,
                      bgcolor: "background.paper",
                      boxShadow: 1,
                      "&:hover": { bgcolor: "error.light", color: "white" },
                    }}
                  >
                    <DeleteIcon fontSize="inherit" />
                  </IconButton>
                </Box>
              ))}
              {galleryUrls.length === 0 && (
                <Chip
                  size="small"
                  variant="outlined"
                  label="No photo URLs added yet"
                />
              )}
            </Stack>
          </Grid>
        </Grid>

        {/* Actions */}
        <Stack
          direction="row"
          spacing={2}
          justifyContent="flex-end"
          sx={{ mt: 4 }}
        >
          <Button variant="text" color="inherit" onClick={handleReset}>
            Reset
          </Button>
          <Button type="submit" variant="contained" size="large">
            Save Product
          </Button>
        </Stack>
      </Box>
    </Paper>
  );
}
